import { Star, Sparkle, Video, BookText, Brain, Medal, BarChart, Clock, Users, Award, Trophy } from 'lucide-react';
import { Baby } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import {IMAGE_COURSES, IMAGE_QCM, IMAGE_STATS, IMAGE_VIDEO} from "../utils/image_const.ts";

export default function Hero() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const scrollToConcept = () => {
    const conceptSection = document.getElementById('concept-section');
    if (conceptSection) {
      conceptSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartFree = async () => {
  if (!user) {
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (error) {
      console.error('Authentication failed:', error);
    }
  } else {
    navigate('/dashboard');
  }
};

  const features = [
    {
      icon: Clock,
      title: 'Flexibilité totale',
      description: 'Formez-vous à votre rythme avec un accès 24/7 aux contenus'
    },
    {
      icon: Users,
      title: 'Accompagnement personnalisé',
      description: 'Un suivi individuel pour garantir votre réussite'
    },
    {
      icon: Award,
      title: 'Expertise reconnue',
      description: 'Jury officiel pour la VAE et Formatrice en institut de formation AP'
    }
  ];

  const concepts = [
    {
      icon: Video,
      title: "Des vidéos d'accompagnement",
      description: 'Apprenez avec des vidéos explicatives et des démonstrations pratiques',
        image : IMAGE_VIDEO,
    },
    {
      icon: BookText,
      title: 'Des cours textuels à votre rythme',
      description: 'Accédez à des contenus détaillés que vous pouvez étudier selon votre emploi du temps',
        image: IMAGE_COURSES
    },
    {
      icon: Brain,
      title: 'Des QCM pour vous auto-évaluer',
      description: 'Testez vos connaissances régulièrement avec des quiz interactifs',
        image: IMAGE_QCM
    },
    {
      icon: BarChart,
      title: 'Des statistiques de progression',
      description: 'Suivez votre évolution et identifiez vos points forts et axes d\'amélioration',
        image: IMAGE_STATS
    }
  ];

  const testimonials = [
    {
      icon: Baby,
      name: 'Alexia',
      role: 'CAP Petite Enfance',
      content: 'Je souhaitais prendre connaissance des nouvelles techniques, me remettre à niveau et passer ma VAE d\'AP, grâce à ce site, je peux faire les deux à mon rythme.'
    },
    {
      icon: Trophy,
      name: 'Julie',
      role: 'CAP Petite Enfance',
      content: 'En préparant le diplôme d\'auxiliaire de puériculture, j\'ai découvert ce site proposant des QCM spécialement conçus pour notre formation. Dès les premières utilisations, j\'ai été agréablement surprise par la clarté des questions, la variété des thèmes abordés et la pertinence par rapport au programme officiel. Ce qui m\'a le plus aidée, c\'est la possibilité de m\'entraîner à mon rythme, de repérer mes erreurs et de comprendre immédiatement les corrections. Grâce à ce site, je me suis sentie mieux préparée, plus sereine et motivée. C\'est un outil que je recommande vivement à toutes les personnes qui préparent le diplôme d\'auxiliaire de puériculture, que ce soit en formation initiale ou en VAE.'
    }
  ];

  return (
    <>
      {/* Balises SEO pour la home */}
      <Helmet>
        <title>L’Oustalet Formation – VAE Auxiliaire de puériculture & CAP AEPE</title>
        <meta
          name="description"
          content="Accompagnement VAE Auxiliaire de puériculture (DEAP) et préparation CAP AEPE. Orals blancs, relecture Livret 2, financements (CPF)."
        />
        <link rel="canonical" href="https://www.loustaletformation.fr/" />
      </Helmet>
    <div className="bg-white" style={{ fontFamily: font.family }}>
      {/* Hero Section */}
      <div className={`bg-${theme.secondary} py-20 relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-60 z-0">
          <Star className="absolute top-10 left-10 w-24 h-24 transform -rotate-12 text-white" />
          <Medal className="absolute bottom-10 left-1/4 w-24 h-24 transform -rotate-12 text-white" />
          <Sparkle className="absolute bottom-10 right-10 w-32 h-32 transform rotate-12 text-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
                <h1 className="sr-only">
                    VAE Auxiliaire de puériculture & CAP AEPE – L’Oustalet Formation
                </h1>
                <p className={`h1-font text-4xl sm:text-5xl font-bold text-rose-600 mb-8`}>
                    Votre avenir dans la petite enfance<br/>commence ici
                </p>
                <p className="text-lg font-semibold text-rose-700 mb-4">
                    Inscription gratuite&nbsp;: Tout le site accessible gratuitement pendant 7 jours !
                </p>
                <p className="text-sm text-gray-600 mb-16">
                    ✅ Sans engagement · ✅ Aucun moyen de paiement requis
                </p>
                <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
                    Obtenez votre VAE avec un accompagnement personnalisé et des formations adaptées à vos besoins
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-16">
                    <button
                        onClick={handleStartFree}
                        className={`w-full sm:w-auto bg-rose-600 text-white px-8 py-4 rounded-lg hover:bg-rose-700 transition-colors text-lg font-medium`}
                    >
                        Commencer gratuitement
                    </button>
                    <button
                        onClick={scrollToConcept}
                        className={`w-full sm:w-auto bg-white text-rose-700 px-8 py-4 rounded-lg border-2 border-rose-700 hover:bg-rose-50 transition-colors text-lg font-medium`}
                    >
                        Découvrir notre accompagnement
                    </button>
                    <button
                        onClick={() => navigate('/faq')}
                        className={`w-full sm:w-auto bg-white text-rose-700 px-8 py-4 rounded-lg border-2 border-rose-700 hover:bg-rose-50 transition-colors text-lg font-medium`}
                    >
                        Voir la FAQ
                    </button>
                </div>
            </div>
        </div>
      </div>

      {/* Features Section */}
      {/* Video Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold text-${theme.text} mb-4`}>
              Découvrez notre méthode d'accompagnement
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une approche personnalisée pour réussir votre VAE d'auxiliaire de puériculture
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg">
              <iframe
                  title="Bienvenue sur L'oustalet Formation"
                src="https://player.vimeo.com/video/1106419711?h=ed62d1c569&dnt=1"
                className="w-full h-full"
                  loading="lazy"
                  allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 z-0">
          <Trophy className="absolute top-10 left-5 w-20 h-28 text-rose-400" />
          <Star className="absolute top-5 right-10 w-24 h-24 text-rose-400" />
          <Star className="absolute bottom-20 left-1/4 w-28 h-20 text-rose-400" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-full shadow-sm border border-gray-100 aspect-square flex flex-col items-center justify-center hover:shadow-lg transition-shadow">
                <div className={`w-16 h-16 rounded-full bg-${theme.secondary} flex items-center justify-center mb-6 mx-auto`}>
                  <feature.icon className={`h-6 w-6 text-${theme.text}`} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center">{feature.title}</h3>
                <p className="text-gray-600 text-center text-sm max-w-[80%]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Preview Section */}
      <div className={`bg-${theme.secondary} py-20 relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-50 z-0">
          <Star className="absolute top-10 left-10 w-24 h-24 text-white" />
          <Medal className="absolute top-20 right-20 w-20 h-20 text-white" />
          <Star className="absolute bottom-20 left-1/3 w-28 h-28 text-white" />
          <Medal className="absolute bottom-10 right-1/4 w-24 h-24 text-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 id="concept-section" className={`text-3xl font-bold text-center mb-12 text-${theme.text}`}>
            Notre concept
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {concepts.map((concept, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48">
                  <img src={concept.image} alt={concept.title} className="w-full h-full object-cover" />
                  <div className={`absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center`}>
                    <concept.icon className="h-12 w-12 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2 text-center">{concept.title}</h3>
                  <p className="text-gray-600 text-center text-sm">{concept.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 z-0">
          <Sparkle className="absolute top-20 left-1/4 w-24 h-24 transform -rotate-12 text-rose-400" />
          <Star className="absolute top-40 right-1/3 w-20 h-20 text-rose-400" />
          <Trophy className="absolute bottom-20 left-20 w-28 h-28 text-rose-400" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className={`text-3xl font-bold text-center mb-12 text-${theme.text}`}>
            Ce que disent nos apprenants
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <div className={`w-16 h-16 rounded-full bg-${theme.secondary} flex items-center justify-center mb-4 mx-auto`}>
                  <testimonial.icon className={`h-8 w-8 text-${theme.text}`} />
                </div>
                <div className="mb-4">
                  <h3 className="font-semibold text-center">{testimonial.name}</h3>
                  <p className="text-sm text-gray-500 text-center">{testimonial.role}</p>
                </div>
                <p className="text-gray-600 italic text-center">{testimonial.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className={`bg-${theme.secondary} py-20 relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-50 z-0">
          <Star className="absolute top-10 left-1/4 w-24 h-24 text-white" />
          <Medal className="absolute bottom-10 right-1/4 w-24 h-24 text-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className={`text-3xl font-bold mb-8 text-${theme.text}`}>
            Prêt à commencer votre formation ?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Rejoignez notre communauté d'apprenants et transformez votre carrière
          </p>
          <button
              onClick={() => navigate('/tarifs')}
              className={`bg-rose-600 text-white px-8 py-4 rounded-lg hover:bg-rose-700 transition-colors text-lg font-medium`}
            >
              Voir nos abonnements
            </button>
        </div>
      </div>
    </div>
    </>
  );
}